import { z } from 'zod';
import { PROCEDURE_TYPES } from '../case-wiki/civil-quality.js';
import { VisibilitySchema, AccessRoleSchema, CaseShareSchema } from '../resource-sharing/index.js';

/** Persisted values used to archive and restore dossiers across applications. */
export const CASE_ARCHIVED_STATUS = 'archivé';
export const CASE_ACTIVE_STATUS = 'en_cours';

/**
 * Priorités de dossier proposées par le formulaire de création
 * (`apps/legalworkspace/src/components/CaseFormDialog.tsx:32`). `cases.priority`
 * est un `text` OVH sans CHECK — ce catalogue n'est PAS une contrainte DB, c'est
 * le miroir figé des valeurs que l'UI propose, pour qu'un créateur programmatique
 * (agent `create_case`, script, import) ne produise jamais une priorité que
 * l'écran de la fiche dossier ne sait pas afficher/filtrer.
 */
export const CASE_PRIORITIES = ['basse', 'normale', 'haute'] as const;
export const CasePrioritySchema = z.enum(CASE_PRIORITIES);
export type CasePriority = z.infer<typeof CasePrioritySchema>;

/**
 * Matières qu'un créateur programmatique (agent `create_case`, script, import)
 * peut poser sur un dossier : exactement celles du formulaire de création.
 *
 * WHY un alias et pas une liste : `cases.procedure_type` est un `text` libre,
 * `PROCEDURE_TYPES` est sa seule borne. Une copie à six valeurs a survécu à
 * l'ajout de l'immobilier et des assurances : l'agent ne pouvait pas créer un
 * dossier que l'écran proposait (vérification du 2026-11-01).
 *
 * ⚠️ Nom distinct de `CaseProcedureTypeSchema` (`case-wiki/procedure.ts`), qui
 * énumère un vocabulaire wiki interne différent
 * (`civil|penal|commercial|administratif|social|autre`) pour la table
 * `case_procedures`. Les deux catalogues ne se recouvrent pas et ne doivent
 * jamais être confondus : celui-ci porte les libellés affichés de `cases`.
 */
export const CASE_PROCEDURE_TYPE_LABELS = PROCEDURE_TYPES;
export const CaseProcedureTypeLabelSchema = z.enum(CASE_PROCEDURE_TYPE_LABELS);
export type CaseProcedureTypeLabel = z.infer<typeof CaseProcedureTypeLabelSchema>;

/**
 * Contrats de sortie pour les DTO dossier enrichis par le chantier
 * resource-sharing (Phase 2, T9) : la liste (`GET /cases`) et le détail
 * (`GET /cases/:id`) exposent désormais `owner_id`, `visibility` et le rôle
 * résolu de l'appelant (`my_role`), et le détail expose la liste des partages
 * (`shares`) UNIQUEMENT à l'owner.
 *
 * WHY `.passthrough()` : la table `cases` est large (case_summary,
 * arguments_a_mobiliser, demandes_precises, lawyer_profile_override, colonnes
 * droit-public…) et le backend renvoie `SELECT c.*` sans re-projeter. Un schéma
 * `.strict()` rejetterait au parsing côté front toute colonne non listée ici et
 * casserait à chaque ajout de colonne DB. On liste donc les champs stables
 * consommés par l'UI et on tolère le reste — ces schémas servent au TYPAGE front
 * (Phase 3), le backend ne valide pas sa sortie avec eux.
 */

/**
 * Colonnes « socle » d'un dossier communes à la liste et au détail — miroir de
 * l'interface `Case` du front (`apps/legalworkspace/src/types/api.ts`) + les
 * colonnes ownership/visibilité posées par M135.
 */
export const CaseBaseSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string(),
    status: z.string().nullable(),
    description: z.string().nullable(),
    priority: z.string().nullable(),
    deadline: z.string().nullable(),
    client_id: z.string().nullable().optional(),
    client_name: z.string().nullable(),
    specialisation: z.string().nullable().optional(),
    case_summary: z.string().nullable().optional(),
    case_summary_short: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string().nullable().optional(),
    assigned_to: z.string().nullable(),
    procedure_type: z.string().nullable(),
    cabinet_id: z.string().uuid().optional(),
    // Resource-sharing (M135) — présents sur chaque row `cases`.
    owner_id: z.string().uuid().nullable(),
    visibility: VisibilitySchema,
    /**
     * Rôle RÉSOLU de l'appelant sur ce dossier (owner ∪ share ∪ visibilité),
     * calculé par la garde `services/access`. Toujours non-null pour une row
     * renvoyée à l'utilisateur (une row inaccessible est filtrée en amont).
     */
    my_role: AccessRoleSchema,
  })
  .passthrough();

/**
 * Item de la liste `GET /cases` : socle + ownership/visibilité + `my_role` +
 * `shares` optionnel. `shares` n'est présent QUE pour les items dont l'appelant
 * est owner (`my_role === 'owner'`) — le backend le charge en batch (un seul
 * SELECT sur `case_shares` pour tous les dossiers owned de la page, cf.
 * `CaseRepository.getCasesPageByCabinet`) et l'OMET pour tout item non-owned
 * (privacy — la liste des partages d'un dossier n'est jamais exposée à un
 * editor/viewer, cohérent avec le détail `GET /cases/:id`).
 */
export const CaseListItemSchema = CaseBaseSchema.extend({
  shares: z.array(CaseShareSchema).optional(),
});

/** Un item de la liste des dossiers. */
export type CaseListItem = z.infer<typeof CaseListItemSchema>;

/**
 * Détail `GET /cases/:id` : socle + `shares` optionnel. `shares` n'est présent
 * QUE lorsque `my_role === 'owner'` (le backend l'omet pour editor/viewer — ne
 * jamais exposer la liste des partages à un non-owner).
 */
export const CaseDetailSchema = CaseBaseSchema.extend({
  shares: z.array(CaseShareSchema).optional(),
});

/** Le détail d'un dossier (owner voit `shares`). */
export type CaseDetail = z.infer<typeof CaseDetailSchema>;

/**
 * Enveloppe paginée de `GET /cases` (miroir de `getCasesPageByCabinet`).
 * `total` reflète le filtre d'accès (les dossiers privés inaccessibles ne sont
 * pas comptés).
 */
export const CaseListPageSchema = z.object({
  items: z.array(CaseListItemSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  hasMore: z.boolean(),
});

/** Page de liste de dossiers. */
export type CaseListPage = z.infer<typeof CaseListPageSchema>;

/**
 * Ce que la suppression d'un dossier fait aux conversations, lu avant que
 * l'avocat confirme (`GET /api/cases/:id/deletion-impact`, spec #2592,
 * « Suppression d'un dossier »). Deux totaux, jamais de titres : le
 * propriétaire du dossier ne lit pas forcément les conversations privées de
 * ses confrères, mais elles partent avec le dossier et le nombre le dit.
 *
 * Non strict, comme `MemoryUsageSchema` : un champ ajouté plus tard par le
 * backend ne doit pas faire échouer la boîte d'un front plus ancien.
 */
export const CaseDeletionImpactSchema = z.object({
  /** Conversations dont le dossier est le principal : la cascade les supprime avec lui. */
  principalConversationCount: z.number().int().nonnegative(),
  /**
   * Conversations où il figure en dossier connexe, actif ou retiré : elles
   * restent, avec leur historique ; seul leur lien vers lui disparaît.
   */
  relatedConversationCount: z.number().int().nonnegative(),
});

/** Décomptes de l'avertissement de suppression d'un dossier. */
export type CaseDeletionImpact = z.infer<typeof CaseDeletionImpactSchema>;
